/**
 * Klima CH2025 DAILY-LOCAL (MeteoSchweiz und ETH Zürich, CC BY 4.0).
 *
 * Je Station, Variable und Periode eine CSV mit 30 synthetischen Jahren im
 * 365-Tage-Kalender und einer Spalte je Modellkette. Kennzahlen werden je
 * Kette aus den Tageswerten gebildet, die Änderung ist Periode minus Referenz
 * derselben Kette, die Spanne P10/P50/P90 wird über die Ketten gebildet.
 *
 * Fehlende Werte («NA») betreffen die Ketten mit 360-Tage-Modellkalender
 * (HadGEM, 5 Tage je Jahr). Sie sind Kalenderlücken, keine Messausfälle:
 * Summen und Zähler werden auf die volle Tageszahl hochgerechnet, und
 * Hitzeperioden laufen über eine Lücke hinweg, ohne dass sie mitzählt.
 */
import stationsDoc from './ch2025-stations.json';
import { distanceKm, spreadOf, type FutureIndicator, type FutureLevel, type FutureRow, type FutureThresholds } from './types';

export const CH2025_FILES = 'https://rgw.cscs.ch/mchogd:cscs.meteoswiss.ogd.climate/ogd-climate-scenarios-ch2025';
export const CH2025_VERSION = '1.0.0';
export const CH2025_LICENCE = 'CC BY 4.0, MeteoSchweiz und ETH Zürich (2025): Klima CH2025, Daily Datasets, doi:10.18751/climate/scenarios/ch2025/data/1.0/';
export const CH2025_YEARS = 30;
export const CH2025_DAYS = CH2025_YEARS * 365;

export type Ch2025Var = 'tas' | 'tasmin' | 'tasmax';
export type Ch2025Period = 'ref91-20' | 'gwl1.5' | 'gwl2.0' | 'gwl2.5' | 'gwl3.0';
export const CH2025_VARS: Ch2025Var[] = ['tas', 'tasmin', 'tasmax'];
export const CH2025_REF: Ch2025Period = 'ref91-20';
export const CH2025_LEVELS: (FutureLevel & { key: Exclude<Ch2025Period, 'ref91-20'> })[] = [
  { key: 'gwl1.5', label: '+1.5 °C', gwl: 1.5, gwlApprox: false },
  { key: 'gwl2.0', label: '+2.0 °C', gwl: 2.0, gwlApprox: false },
  { key: 'gwl2.5', label: '+2.5 °C', gwl: 2.5, gwlApprox: false },
  { key: 'gwl3.0', label: '+3.0 °C', gwl: 3.0, gwlApprox: false },
];

export interface Ch2025Station {
  id: string;
  abbr: string;
  name: string;
  lat: number;
  lon: number;
  h: number;
}

export const CH2025_STATIONS: Ch2025Station[] = (stationsDoc as { stationen: Ch2025Station[] }).stationen;

const ID_RE = /^[a-z0-9]{2,8}$/;
const PERIOD_RE = /^(ref91-20|gwl1\.5|gwl2\.0|gwl2\.5|gwl3\.0)$/;

/** Dateipfad relativ zum Datensatz; gleiche Regel wie im Worker-Endpunkt. */
export function ch2025Path(station: string, v: Ch2025Var, period: Ch2025Period): string {
  if (!ID_RE.test(station) || !CH2025_VARS.includes(v) || !PERIOD_RE.test(period)) throw new Error('Ungültige CH2025-Angabe');
  return `${station}/ogd-climate-scenarios-ch2025_${station}_${v}_${period}.csv`;
}

export interface Ch2025Csv {
  meta: Record<string, string>;
  chains: string[];
  /** je Kette 10'950 Tageswerte, NaN für «NA» */
  data: Float32Array[];
  missing: number;
}

export function parseCh2025Csv(text: string): Ch2025Csv {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  const meta: Record<string, string> = {};
  let i = 0;
  for (; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith('DATE;')) break;
    const k = l.indexOf(';');
    if (k > 0) meta[l.slice(0, k).trim()] = l.slice(k + 1).trim();
  }
  if (i >= lines.length) throw new Error('CH2025-Datei ohne Datenkopf');
  if (meta.CALENDAR && meta.CALENDAR !== '365_day') throw new Error(`Unerwarteter Kalender ${meta.CALENDAR}`);
  if (meta.UNIT && meta.UNIT !== 'degrees_C') throw new Error(`Unerwartete Einheit ${meta.UNIT}`);
  const chains = lines[i].split(';').slice(1).map((s) => s.trim());
  if (!chains.length) throw new Error('CH2025-Datei ohne Modellketten');
  const data = chains.map(() => new Float32Array(CH2025_DAYS).fill(NaN));
  let row = 0;
  let missing = 0;
  for (let k = i + 1; k < lines.length; k++) {
    const l = lines[k];
    if (!l) continue;
    if (row >= CH2025_DAYS) throw new Error('CH2025-Datei mit zu vielen Tagen');
    const cells = l.split(';');
    if (cells.length !== chains.length + 1) throw new Error(`CH2025-Zeile ${k + 1}: ${cells.length - 1} statt ${chains.length} Werte`);
    if (row === 0 && cells[0] !== '0001-01-01') throw new Error(`CH2025-Datei beginnt mit ${cells[0]}`);
    for (let c = 0; c < chains.length; c++) {
      const s = cells[c + 1];
      if (s === 'NA' || s === '') {
        missing++;
        continue;
      }
      const x = Number(s);
      if (!Number.isFinite(x) || x < -80 || x > 65) throw new Error(`CH2025-Zeile ${k + 1}: ungültiger Wert ${s}`);
      data[c][row] = x;
    }
    row++;
  }
  if (row !== CH2025_DAYS) throw new Error(`CH2025-Datei mit ${row} statt ${CH2025_DAYS} Tagen`);
  return { meta, chains, data, missing };
}

export type ChainMetrics = Record<FutureIndicator, number>;

/** Mittlere Jahreswerte einer Kette über die 30 synthetischen Jahre. */
export function chainMetrics(tas: Float32Array, tasmin: Float32Array, tasmax: Float32Array, t: FutureThresholds): ChainMetrics {
  let hdd = 0;
  let cdd = 0;
  let tSum = 0;
  let nTas = 0;
  let hot = 0;
  let veryHot = 0;
  let nMax = 0;
  let trop = 0;
  let nMin = 0;
  let events = 0;
  let heatDays = 0;
  const txxYear = new Array<number>(CH2025_YEARS).fill(-Infinity);
  let run = 0;
  const closeRun = () => {
    if (run >= t.heatPeriodMinDays) {
      events++;
      heatDays += run;
    }
    run = 0;
  };
  for (let d = 0; d < CH2025_DAYS; d++) {
    const m = tas[d];
    if (m === m) {
      hdd += Math.max(0, t.hddBase - m);
      cdd += Math.max(0, m - t.cddBase);
      tSum += m;
      nTas++;
    }
    const lo = tasmin[d];
    if (lo === lo) {
      nMin++;
      if (lo >= 20) trop++;
    }
    const hi = tasmax[d];
    if (hi === hi) {
      nMax++;
      if (hi >= t.hotDayThreshold) {
        hot++;
        run++;
      } else closeRun();
      if (hi >= 35) veryHot++;
      const y = Math.floor(d / 365);
      if (hi > txxYear[y]) txxYear[y] = hi;
    }
    // Kalenderlücke («NA»): Lauf weder unterbrochen noch verlängert
  }
  closeRun();
  const perYear = (sum: number, n: number) => (n ? (sum * (CH2025_DAYS / n)) / CH2025_YEARS : NaN);
  const txx = txxYear.filter((x) => x > -Infinity);
  return {
    hdd: perYear(hdd, nTas),
    cdd: perYear(cdd, nTas),
    hotDays: perYear(hot, nMax),
    veryHotDays: perYear(veryHot, nMax),
    tropicalNights: perYear(trop, nMin),
    heatEvents: nMax ? events / CH2025_YEARS : NaN,
    heatDays: perYear(heatDays, nMax),
    txx: txx.length ? txx.reduce((a, b) => a + b, 0) / txx.length : NaN,
    tMean: nTas ? tSum / nTas : NaN,
  };
}

export const CH2025_INDICATORS: FutureIndicator[] = ['hdd', 'cdd', 'hotDays', 'veryHotDays', 'tropicalNights', 'heatEvents', 'heatDays', 'txx', 'tMean'];

export function ch2025Definition(ind: FutureIndicator, t: FutureThresholds): { definition: string; unit: string } {
  switch (ind) {
    case 'hdd':
      return { definition: `Summe (${t.hddBase} °C − Tagesmittel), wenn positiv`, unit: 'K·d' };
    case 'cdd':
      return { definition: `Summe (Tagesmittel − ${t.cddBase} °C), wenn positiv`, unit: 'K·d' };
    case 'hotDays':
      return { definition: `Tagesmaximum ≥ ${t.hotDayThreshold} °C`, unit: 'Tage' };
    case 'veryHotDays':
      return { definition: 'Tagesmaximum ≥ 35 °C', unit: 'Tage' };
    case 'tropicalNights':
      return { definition: 'Tagesminimum ≥ 20 °C', unit: 'Tage' };
    case 'heatEvents':
      return { definition: `≥ ${t.heatPeriodMinDays} Tage in Folge mit Tagesmaximum ≥ ${t.hotDayThreshold} °C`, unit: 'Anzahl' };
    case 'heatDays':
      return { definition: `Tage in Hitzeperioden (≥ ${t.heatPeriodMinDays} Tage ≥ ${t.hotDayThreshold} °C)`, unit: 'Tage' };
    case 'txx':
      return { definition: 'höchstes Tagesmaximum des Jahres, Mittel über 30 Jahre', unit: '°C' };
    case 'tMean':
      return { definition: 'Mittel der Tagesmittel', unit: '°C' };
  }
}

export type PeriodFiles = Record<Ch2025Var, Ch2025Csv>;

/** Kennzahlen je Kette (nach Name) für eine Periode. */
export function periodMetrics(files: PeriodFiles, t: FutureThresholds): Map<string, ChainMetrics> {
  const out = new Map<string, ChainMetrics>();
  for (const [c, name] of files.tasmax.chains.entries()) {
    const iTas = files.tas.chains.indexOf(name);
    const iMin = files.tasmin.chains.indexOf(name);
    if (iTas < 0 || iMin < 0) continue;
    out.set(name, chainMetrics(files.tas.data[iTas], files.tasmin.data[iMin], files.tasmax.data[c], t));
  }
  return out;
}

/** Tabellenzeilen: Referenz der Quelle (Median) und Änderung je Stufe über die Ketten. */
export function ch2025Rows(ref: Map<string, ChainMetrics>, levels: Map<string, Map<string, ChainMetrics>>, t: FutureThresholds): FutureRow[] {
  return CH2025_INDICATORS.map((ind) => {
    const deltas: FutureRow['deltas'] = {};
    for (const [key, fut] of levels) {
      const d: number[] = [];
      for (const [name, m] of fut) {
        const r = ref.get(name);
        if (r) d.push(m[ind] - r[ind]);
      }
      deltas[key] = spreadOf(d);
    }
    const refSpread = spreadOf([...ref.values()].map((m) => m[ind]));
    return { indicator: ind, ...ch2025Definition(ind, t), sourceRef: refSpread ? refSpread.p50 : null, deltas };
  });
}

export const MAX_ELEVATION_DIFF = 300;
export const MAX_DISTANCE_KM = 30;

export interface StationChoice {
  station: Ch2025Station;
  distanceKm: number;
  elevationDiffM: number;
  representative: boolean;
  reason: string;
}

/**
 * Stationswahl: nächste Station mit Höhenunterschied ≤ 300 m; sonst die
 * höhenähnlichste im Umkreis von 30 km; sonst die nächste. Ausserhalb der
 * Grenzen gilt die Wahl als eingeschränkt repräsentativ.
 */
export function chooseStation(lat: number, lon: number, elevation: number, stations = CH2025_STATIONS): StationChoice {
  const all = stations
    .map((s) => ({ station: s, distanceKm: distanceKm(lat, lon, s.lat, s.lon), elevationDiffM: s.h - elevation }))
    .sort((a, b) => a.distanceKm - b.distanceKm);
  const near = all.find((c) => Math.abs(c.elevationDiffM) <= MAX_ELEVATION_DIFF);
  if (near && near.distanceKm <= MAX_DISTANCE_KM) return { ...near, representative: true, reason: 'nächste Station mit ähnlicher Höhe' };
  const within = all.filter((c) => c.distanceKm <= MAX_DISTANCE_KM).sort((a, b) => Math.abs(a.elevationDiffM) - Math.abs(b.elevationDiffM));
  if (within.length) {
    const c = within[0];
    return { ...c, representative: Math.abs(c.elevationDiffM) <= MAX_ELEVATION_DIFF, reason: 'höhenähnlichste Station im Umkreis von 30 km' };
  }
  if (near && near.distanceKm <= 2 * MAX_DISTANCE_KM) return { ...near, representative: false, reason: 'nächste Station mit ähnlicher Höhe, weiter als 30 km' };
  return { ...all[0], representative: false, reason: 'nächste Station' };
}
